import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, Link } from "@tanstack/react-router";
import { Badge, Button, IconArrowLeft, Notice, Plate, TextField, type BadgeProps } from "@wilco/ui";
import { useState } from "react";
import { ConfirmDialog, type Confirm } from "../ConfirmDialog.tsx";
import { formatDate, formatNumber, t, type MessageKey } from "../i18n.ts";
import { problemCode } from "../rest.ts";
import { errorNotice, type NoticeState } from "../stock/ShelfPage.tsx";
import { productionApi } from "./api.ts";
import { PositionText } from "./PositionText.tsx";
import { moQuery, PRODUCTION } from "./queries.ts";
import type { ManufacturingOrder, OrderState, Unit, UnitState } from "./types.ts";

const route = getRouteApi("/app/production/orders/$id");

export const ORDER_STATE: Record<OrderState, { key: MessageKey; tone: BadgeProps["tone"] }> = {
  DRAFT: { key: "mo.state.DRAFT", tone: "neutral" },
  CONFIRMED: { key: "mo.state.CONFIRMED", tone: "strong" },
  DONE: { key: "mo.state.DONE", tone: "success" },
  CANCELLED: { key: "mo.state.CANCELLED", tone: "neutral" },
};

const UNIT_STATE: Record<UnitState, { key: MessageKey; tone: BadgeProps["tone"] }> = {
  PLANNED: { key: "unit.state.PLANNED", tone: "neutral" },
  ASSEMBLED: { key: "unit.state.ASSEMBLED", tone: "info" },
  PACKED: { key: "unit.state.PACKED", tone: "success" },
  WRITTEN_OFF: { key: "unit.state.WRITTEN_OFF", tone: "neutral" },
  CANCELLED: { key: "unit.state.CANCELLED", tone: "neutral" },
};

/**
 * Заказ на производство: выпуск в два шага (решение D). «Зібрано»
 * списывает с «Лінії» и ставит полуфабрикат на НЗВ (ВР-08..ВР-10),
 * «Упаковано» списывает коробку с поста «Пакування» и переводит изделие на
 * «Готову продукцію» (ВР-11). Цен на экране нет (ВР-16).
 */
export function MoPage() {
  const { id } = route.useParams();
  const queryClient = useQueryClient();
  const mo = useQuery(moQuery(id));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const run = useMutation({ mutationFn: (fn: () => Promise<unknown>) => fn() });

  const act = (fn: () => Promise<unknown>, ok: (r: unknown) => NoticeState, onError?: (e: unknown) => boolean) =>
    run.mutate(fn, {
      onSuccess: (r) => {
        setNotice(ok(r));
        setSelected(new Set());
        void queryClient.invalidateQueries({ queryKey: PRODUCTION });
      },
      onError: (e) => {
        if (!onError?.(e)) setNotice(errorNotice(e));
      },
    });

  const o = mo.data;
  if (mo.error) return <Notice tone="danger" title={errorNotice(mo.error).title} className="page" />;
  if (!o) return null;

  const active = o.state === "CONFIRMED";
  const short = o.kit.filter((k) => k.short).length;
  const ready = o.units.filter((u) => u.state === "ASSEMBLED" && u.position.routeDone && !u.position.quarantined).length;
  const routeDone = o.units.filter((u) => u.position.routeDone).length;

  // С незакрытым маршрутом «Упаковано» переспрашивает (МР-18).
  const pack = (serial: string, routeClosed = true) =>
    act(
      () => productionApi.pack(serial, routeClosed),
      () => ({ tone: "success", title: t("mo.packedOne", { serial }) }),
      (e) => {
        if (problemCode(e) !== "step.packing_open_route") return false;
        setConfirm({ title: t("mo.openRoute.title"), body: errorNotice(e).title, action: t("mo.openRoute.action"), run: () => pack(serial, false) });
        return true;
      },
    );

  return (
    <div className="page mo">
      <div className="page-head">
        <Button asChild variant="ghost" size="icon">
          <Link to="/production/orders" aria-label={t("mo.back")}>
            <IconArrowLeft />
          </Link>
        </Button>
        <h1 className="page-title w-num">{o.number}</h1>
        <Badge tone={ORDER_STATE[o.state].tone}>{t(ORDER_STATE[o.state].key)}</Badge>
      </div>

      <Plate cut="lg" className="order-head">
        <dl className="order-meta">
          <Meta label={t("mo.product")} value={`${o.productName}${o.version ? ` ${o.version}` : ""} × ${String(o.qty)}`} />
          <Meta label={t("mo.warehouse")} value={o.warehouseName} />
          <Meta label={t("mo.start")} value={formatDate(o.start)} />
          {o.salesOrder ? <Meta label={t("mo.salesOrder")} value={o.salesOrder} /> : null}
          {o.batch ? <Meta label={t("mo.batch")} value={o.batch} /> : null}
        </dl>
      </Plate>

      {notice ? <Notice tone={notice.tone} title={notice.title} className="stock-notice" /> : null}
      {o.state === "DRAFT" ? (
        // Черновик из «Нове замовлення»: код партии и заявки складу появятся после подтверждения.
        <Notice tone="info" title={t("mo.draft.title")} className="stock-notice">
          <div className="notice-actions">
            <Button variant="primary" disabled={run.isPending} onClick={() => act(() => productionApi.confirm(o.id), () => ({ tone: "success", title: t("mo.confirmed") }))}>
              {t("mo.confirm")}
            </Button>
            <Button
              variant="ghost"
              disabled={run.isPending}
              onClick={() =>
                setConfirm({ title: t("mo.cancel.title", { number: o.number }), body: t("mo.cancel.body"), action: t("mo.cancel"), run: () => act(() => productionApi.cancel(o.id), () => ({ tone: "success", title: t("mo.cancelled") })) })
              }
            >
              {t("mo.cancel")}
            </Button>
          </div>
        </Notice>
      ) : null}
      {o.notes.map((n) => (
        <Notice key={n} tone="warning" title={n} className="stock-notice" />
      ))}

      {o.kit.length > 0 ? (
        <section className="stock-section" aria-labelledby="mo-kit">
          <div className="section-head">
            <h2 id="mo-kit" className="desk-group-title">
              {t("mo.kit")} · {t("mo.notOnLine", { count: short })}
            </h2>
            {short > 0 && !o.kitRequest ? (
              // Пока чего-то нет на линии, главная кнопка выдаёт комплект (ВР-04).
              <Button variant="primary" disabled={run.isPending} onClick={() => act(() => productionApi.kit(o.id), () => ({ tone: "success", title: t("mo.kitIssued") }))}>
                {t("mo.issueKit")}
              </Button>
            ) : null}
          </div>
          {o.kitRequest && short > 0 ? <p className="w-muted">{t("line.kitRequest", { request: o.kitRequest })}</p> : null}
          <div className="kit-lines" role="table" aria-labelledby="mo-kit">
            <div className="kit-line" role="row" data-head="">
              <span role="columnheader">{t("order.product")}</span>
              <span role="columnheader" className="num">
                {t("mo.need")}
              </span>
              <span role="columnheader" className="num">
                {t("mo.onLine")}
              </span>
            </div>
            {o.kit.map((k) => (
              <div key={k.productId} className="kit-line" role="row" data-short={k.short ? "" : undefined}>
                <span role="cell" className="kit-name">
                  <span className="w-num shelf-sku">{k.productSku}</span> {k.productName}
                  {k.packing ? <span className="w-muted">{` · ${t("mo.packing")}`}</span> : null}
                </span>
                <span role="cell" className="num w-num" data-label={t("mo.need")}>
                  {`${formatNumber(k.need)}\u00a0${k.uom}`}
                </span>
                <span role="cell" className="num w-num" data-label={t("mo.onLine")}>
                  {k.packing ? "—" : `${formatNumber(k.onLine)}\u00a0${k.uom}`}
                </span>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <section className="stock-section" aria-labelledby="mo-units">
        <div className="section-head">
          <h2 id="mo-units" className="desk-group-title">
            {t("mo.units")}
            {o.units.length > 0 && !o.prototype ? ` · ${t("mo.routeDone", { done: routeDone, total: o.units.length })}` : ""}
          </h2>
        </div>
        {o.units.length === 0 && active ? (
          <div className="order-actions">
            <Button variant={short > 0 ? "secondary" : "primary"} disabled={run.isPending} onClick={() => act(() => productionApi.serials(o.id), (s) => ({ tone: "success", title: t("mo.serialsPrinted", { count: (s as string[]).length }) }))}>
              {t("mo.printLabels")}
            </Button>
          </div>
        ) : null}
        <ul className="units">
          {o.units.map((u) => (
            <UnitRow
              key={u.serial}
              order={o}
              unit={u}
              checked={selected.has(u.serial)}
              onCheck={(v) =>
                setSelected((prev) => {
                  const next = new Set(prev);
                  if (v) next.add(u.serial);
                  else next.delete(u.serial);
                  return next;
                })
              }
              busy={run.isPending}
              onPack={() => pack(u.serial)}
              onWriteOff={(reason) => act(() => productionApi.writeOff(u.serial, reason), () => ({ tone: "success", title: t("mo.writtenOff", { serial: u.serial }) }))}
            />
          ))}
        </ul>
        {active && o.units.length > 0 ? (
          <div className="order-actions">
            <Button
              variant={selected.size > 0 ? "primary" : "secondary"}
              disabled={selected.size === 0 || run.isPending}
              onClick={() => act(() => productionApi.assemble([...selected]), () => ({ tone: "success", title: t("mo.assembled", { count: selected.size }) }))}
            >
              {t("mo.assemble", { count: selected.size })}
            </Button>
            {!o.prototype ? (
              <Button
                disabled={ready === 0 || run.isPending}
                onClick={() => act(() => productionApi.packReady(o.id), (s) => ({ tone: "success", title: t("mo.packed", { serials: (s as string[]).join(", ") }) }))}
              >
                {t("mo.packReadyCount", { count: ready })}
              </Button>
            ) : null}
            <Button disabled={run.isPending} onClick={() => act(() => productionApi.addUnits(o.id, 1), () => ({ tone: "success", title: t("mo.unitAdded") }))}>
              {t("mo.addUnit")}
            </Button>
            <Button variant="ghost" disabled={run.isPending} onClick={() => act(() => productionApi.cancel(o.id), () => ({ tone: "info", title: t("mo.cancelled") }))}>
              {t("mo.cancel")}
            </Button>
          </div>
        ) : null}
      </section>
      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="order-meta-item">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function UnitRow({
  order,
  unit,
  checked,
  onCheck,
  busy,
  onPack,
  onWriteOff,
}: {
  order: ManufacturingOrder;
  unit: Unit;
  checked: boolean;
  onCheck: (v: boolean) => void;
  busy: boolean;
  onPack: () => void;
  onWriteOff: (reason: string) => void;
}) {
  const [reason, setReason] = useState("");
  const canAssemble = unit.state === "PLANNED" && order.state === "CONFIRMED";
  return (
    <li className="unit">
      <label className="unit-check" data-disabled={canAssemble ? undefined : ""}>
        <input type="checkbox" checked={checked} disabled={!canAssemble} onChange={(e) => onCheck(e.target.checked)} aria-label={t("mo.select", { serial: unit.serial })} />
      </label>
      <span className="unit-main">
        <Link to="/production/unit" search={{ serial: unit.serial }} className="w-num unit-serial">
          {unit.serial}
        </Link>
        {order.prototype ? null : <PositionText position={unit.position} />}
      </span>
      <Badge tone={UNIT_STATE[unit.state].tone}>{t(UNIT_STATE[unit.state].key)}</Badge>
      <span className="unit-actions">
        {unit.state === "ASSEMBLED" && !order.prototype ? (
          <Button disabled={busy} onClick={onPack}>
            {t("mo.pack")}
          </Button>
        ) : null}
        {unit.state === "ASSEMBLED" && order.prototype ? (
          <span className="unit-writeoff">
            <TextField label={t("mo.writeOffReason")} hideLabel placeholder={t("mo.writeOffReason")} value={reason} onChange={(e) => setReason(e.target.value)} />
            <Button disabled={busy || !reason.trim()} onClick={() => onWriteOff(reason)}>
              {t("mo.writeOff")}
            </Button>
          </span>
        ) : null}
      </span>
    </li>
  );
}
